'use client';

import { ChevronDown, ChevronLeft, ChevronRight, Layers, RotateCcw } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormatPrice } from '@/components/ui/FormatPrice';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import { detectRegion } from '../utils/regionColors';
import { HorizonOverlay } from './PropertyCard';

// =============================================================================
// SHARED TYPES
// =============================================================================

export interface PropertyNavigationItem {
  id: string;
  name: string;
  description?: string | null;
  location?: string;
  primaryImageUrl?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  totalFloorArea?: number | null;
  currency?: string | null;
  currentAskingPrice?: number | null;
  attributeValues?: {
    askingPrice?: {
      valueNumber?: number | null;
    };
  } | null;
}

interface FormattedProperty {
  id: string;
  image: string;
  name: string;
  location: string;
  bedrooms: number;
  area: string | null;
  price: number | null;
  currency: string;
  isFrance: boolean;
  description: string;
}

// Helper to format property for display
function formatProperty(property: PropertyNavigationItem): FormattedProperty {
  const price = property.attributeValues?.askingPrice?.valueNumber ?? property.currentAskingPrice ?? null;
  const currency = property.currency || DEFAULT_CURRENCY;
  const area = property.totalFloorArea ? `${property.totalFloorArea}` : null;
  const location = property.location || property.description || 'France';
  const region = detectRegion(location);

  return {
    id: property.id,
    image: property.primaryImageUrl || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&h=1080&fit=crop',
    name: property.name,
    location,
    bedrooms: property.bedrooms || 0,
    area,
    price,
    currency,
    isFrance: region === 'provence',
    description: property.description || '',
  };
}

// =============================================================================
// SHARED CARD COMPONENTS
// =============================================================================

interface CardProps {
  property: FormattedProperty;
  locale: string;
  variant?: 'classic' | 'cinema' | 'editorial';
  onClick?: (() => void) | undefined;
}

/**
 * Classic Card - Portrait 3:4 with floating View button
 */
function ClassicCard({ property, locale, onClick }: CardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const content = (
    <div 
      className="group relative w-full aspect-3/4 overflow-hidden bg-stone-200 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <Image 
        src={property.image}
        alt={property.name}
        fill
        className={`object-cover transition-transform duration-2000 ease-out ${isHovered ? 'scale-105' : 'scale-100'}`}
        sizes="(max-width: 768px) 100vw, 50vw"
      />

      <HorizonOverlay isHovered={isHovered} isFrance={property.isFrance} />

      <div className="absolute bottom-8 left-8 text-white z-40">
        <h3 className="font-serif text-2xl drop-shadow-lg">{property.name}</h3>
        <p className="text-xs uppercase tracking-widest mt-2 opacity-90 drop-shadow-md">{property.location}</p>
      </div>
      
      {/* Floating View Button */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 flex items-center justify-center pointer-events-none">
        <div className={`relative flex items-center justify-center transition-all duration-700 ease-[0.19,1,0.22,1] ${isHovered ? 'w-24 h-24 opacity-100' : 'w-12 h-12 opacity-0 translate-y-4'}`}>
          <div className="absolute inset-0 border-[0.5px] border-white/60 rounded-full animate-[spin_12s_linear_infinite]" />
          <span className="text-white font-serif italic text-xl tracking-widest drop-shadow-md">View</span>
        </div>
      </div>
    </div>
  );

  if (onClick) return content;
  return <Link href={`/${locale}/properties/${property.id}`}>{content}</Link>;
}

/**
 * Cinema Card - Wide 16:9 with bottom info bar
 */
function CinemaCard({ property, locale, onClick }: CardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const content = (
    <div 
      className="group relative w-full aspect-video overflow-hidden bg-stone-200 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <Image 
        src={property.image}
        alt={property.name}
        fill
        className={`object-cover transition-transform duration-2000 ease-out ${isHovered ? 'scale-105' : 'scale-100'}`}
        sizes="(max-width: 768px) 85vw, 60vw"
      />

      <HorizonOverlay isHovered={isHovered} isFrance={property.isFrance} />

      <div className="absolute bottom-8 left-8 right-8 text-white z-40 flex justify-between items-end">
        <div>
          <h3 className="font-serif text-3xl md:text-4xl drop-shadow-lg">{property.name}</h3>
          <div className={`flex items-center gap-4 mt-2 transition-all duration-700 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <p className="text-xs uppercase tracking-widest opacity-90">{property.location}</p>
            {property.price && (
              <>
                <span className="w-px h-3 bg-white/50" />
                <FormatPrice amount={property.price} currency={property.currency} size="sm" className="text-white! text-xs uppercase tracking-widest opacity-90" />
              </>
            )}
          </div>
        </div>
        <svg 
          className={`w-6 h-6 transition-transform duration-500 ${isHovered ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
        </svg>
      </div>
    </div>
  );

  if (onClick) return content;
  return <Link href={`/${locale}/properties/${property.id}`}>{content}</Link>;
}

/**
 * Editorial Card - Image with overlapping text box
 */
function EditorialCard({ property, locale, onClick }: CardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const content = (
    <div 
      className="group relative w-full cursor-pointer p-4 md:p-8"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <div className="relative w-full aspect-4/5 md:w-[90%] overflow-hidden bg-stone-200">
        <Image 
          src={property.image}
          alt={property.name}
          fill
          className={`object-cover transition-transform duration-2000 ease-out ${isHovered ? 'scale-105' : 'scale-100'}`}
          sizes="(max-width: 768px) 100vw, 66vw"
        />
        <HorizonOverlay isHovered={isHovered} isFrance={property.isFrance} />
      </div>

      {/* Overlapping Text Box */}
      <div className="absolute bottom-0 right-0 md:right-4 w-3/4 md:w-1/2 bg-stone-50 p-6 shadow-2xl transition-transform duration-700 ease-out group-hover:-translate-y-2 z-40">
        <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">{property.location}</p>
        <h3 className="font-serif text-2xl md:text-3xl text-stone-900 leading-none mb-4">{property.name}</h3>
        <div className="flex justify-between items-center border-t border-stone-200 pt-4">
          {property.price && (
            <FormatPrice amount={property.price} currency={property.currency} size="sm" className="text-stone-900! font-sans text-xs font-medium" />
          )}
          <span className={`text-[10px] uppercase tracking-widest transition-colors duration-300 ${isHovered ? 'text-stone-900' : 'text-stone-400'}`}>
            Explore
          </span>
        </div>
        {/* Decorative Line */}
        <div 
          className={`absolute top-0 left-0 h-0.5 transition-all duration-700 ${isHovered ? 'w-full' : 'w-0'}`} 
          style={{ backgroundColor: property.isFrance ? '#002654' : '#EA2839' }} 
        />
      </div>
    </div>
  );

  if (onClick) return content;
  return <Link href={`/${locale}/properties/${property.id}`}>{content}</Link>;
}

// =============================================================================
// 1. PROPERTY REEL - Horizontal Scroll Carousel
// =============================================================================

export interface PropertyReelProps {
  properties: PropertyNavigationItem[];
  locale?: string;
  title?: string;
  subtitle?: string;
  onPropertyClick?: (property: PropertyNavigationItem) => void;
  maxItems?: number;
}

/**
 * PropertyReel - Horizontal scroll carousel with cinema-style cards
 * 
 * Sample UI "The Reel" (EXACT MATCH):
 * - Horizontal overflow with snap scrolling
 * - Cinema-style wide cards
 * - Scroll indicator with icon
 */
export function PropertyReel({
  properties,
  locale = 'en',
  title,
  subtitle,
  onPropertyClick,
  maxItems = 10,
}: PropertyReelProps) {
  const formatted = useMemo(() => 
    properties.slice(0, maxItems).map(formatProperty), 
    [properties, maxItems]
  );

  if (formatted.length === 0) return null;

  return (
    <section className="w-full py-16 bg-stone-50">
      {(title || subtitle) && (
        <div className="container mx-auto px-6 md:px-12 mb-8">
          {subtitle && <p className="text-stone-400 uppercase tracking-[0.3em] text-xs mb-2">{subtitle}</p>}
          {title && <h2 className="font-serif text-3xl md:text-4xl text-stone-900">{title}</h2>}
        </div>
      )}
      
      <div className="flex items-center gap-2 mb-4 text-stone-400 px-6 md:px-12">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
        </svg>
        <span className="text-xs uppercase tracking-widest">Scroll Horizontally / Swipe</span>
      </div>
      
      {/* Horizontal Reel Container */}
      <div className="flex overflow-x-auto gap-6 px-6 md:px-12 pb-12 snap-x snap-mandatory no-scrollbar cursor-grab active:cursor-grabbing">
        {formatted.map((prop) => (
          <div key={prop.id} className="min-w-[85vw] md:min-w-[60vw] snap-center">
            <CinemaCard 
              property={prop} 
              locale={locale}
              onClick={onPropertyClick ? () => onPropertyClick(properties.find(p => p.id === prop.id)!) : undefined}
            />
          </div>
        ))}
        {/* Spacer for end of scroll */}
        <div className="min-w-[10vw]" />
      </div>
    </section>
  );
}

// =============================================================================
// 2. PROPERTY JOURNAL - Staggered Vertical Layout
// =============================================================================

export interface PropertyJournalProps {
  properties: PropertyNavigationItem[];
  locale?: string;
  title?: string;
  subtitle?: string;
  onPropertyClick?: (property: PropertyNavigationItem) => void;
  maxItems?: number;
}

/**
 * PropertyJournal - Staggered vertical layout alternating editorial/classic
 * 
 * Sample UI "The Journal" (EXACT MATCH):
 * - Alternating left/right alignment
 * - Mix of editorial and classic card styles
 * - Large numbered backgrounds
 */
export function PropertyJournal({
  properties,
  locale = 'en',
  title,
  subtitle,
  onPropertyClick,
  maxItems = 10,
}: PropertyJournalProps) {
  const formatted = useMemo(() => 
    properties.slice(0, maxItems).map(formatProperty), 
    [properties, maxItems]
  );

  if (formatted.length === 0) return null;

  return (
    <section className="w-full py-16 bg-stone-50">
      {(title || subtitle) && (
        <div className="container mx-auto px-6 md:px-12 mb-16 text-center">
          {subtitle && <p className="text-stone-400 uppercase tracking-[0.3em] text-xs mb-4">{subtitle}</p>}
          {title && <h2 className="font-serif text-3xl md:text-4xl text-stone-900 italic">{title}</h2>}
        </div>
      )}

      <div className="w-full max-w-5xl mx-auto px-6 md:px-12">
        <div className="flex flex-col gap-24 md:gap-40">
          {formatted.map((prop, idx) => (
            <div key={prop.id} className={`flex ${idx % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
              <div className="w-full md:w-2/3">
                {idx % 2 === 0 ? (
                  <EditorialCard 
                    property={prop} 
                    locale={locale}
                    onClick={onPropertyClick ? () => onPropertyClick(properties.find(p => p.id === prop.id)!) : undefined}
                  />
                ) : (
                  <div className="relative">
                    <div className="absolute -left-12 top-12 text-stone-200 font-serif text-9xl -z-10 select-none">
                      {`0${idx + 1}`}
                    </div>
                    <ClassicCard 
                      property={prop} 
                      locale={locale}
                      onClick={onPropertyClick ? () => onPropertyClick(properties.find(p => p.id === prop.id)!) : undefined}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// 3. PROPERTY SPOTLIGHT - Centered Carousel
// =============================================================================

export interface PropertySpotlightProps {
  properties: PropertyNavigationItem[];
  locale?: string;
  title?: string;
  subtitle?: string;
  onPropertyClick?: (property: PropertyNavigationItem) => void;
  maxItems?: number;
}

/**
 * PropertySpotlight - Centered carousel with blur background
 * 
 * Sample UI "The Spotlight" (EXACT MATCH):
 * - Blurred background from current image
 * - Active card is larger and focused
 * - Inactive cards are smaller, grayscale
 * - Navigation controls at bottom
 */
export function PropertySpotlight({
  properties,
  locale: _locale = 'en',
  title,
  subtitle,
  onPropertyClick,
  maxItems = 10,
}: PropertySpotlightProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  
  const formatted = useMemo(() => 
    properties.slice(0, maxItems).map(formatProperty), 
    [properties, maxItems]
  );

  const next = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % formatted.length);
  }, [formatted.length]);

  const prev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + formatted.length) % formatted.length);
  }, [formatted.length]);

  if (formatted.length === 0) return null;

  return (
    <section className="w-full py-16 bg-stone-100">
      {(title || subtitle) && (
        <div className="container mx-auto px-6 md:px-12 mb-8 text-center">
          {subtitle && <p className="text-stone-400 uppercase tracking-[0.3em] text-xs mb-4">{subtitle}</p>}
          {title && <h2 className="font-serif text-3xl md:text-4xl text-stone-900">{title}</h2>}
        </div>
      )}

      <div className="w-full h-[70vh] relative flex items-center justify-center overflow-hidden bg-stone-200">
        {/* Background Blur Layer */}
        <div className="absolute inset-0 overflow-hidden">
          {formatted[activeIndex] && (
            <Image 
              src={formatted[activeIndex].image}
              alt=""
              fill
              className="object-cover blur-3xl opacity-30 scale-125 transition-all duration-1000"
            />
          )}
        </div>

        {/* Carousel Track */}
        <div 
          className="flex items-center gap-12 md:gap-24 transition-transform duration-700 ease-[0.22,1,0.36,1]"
          style={{ transform: `translateX(calc(50% - ${activeIndex * (320 + 96) + 160}px))` }}
        >
          {formatted.map((prop, idx) => {
            const isActive = idx === activeIndex;
            return (
              <div 
                key={prop.id}
                className={`relative shrink-0 transition-all duration-700 ease-[0.22,1,0.36,1] cursor-pointer ${
                  isActive 
                    ? 'w-[80vw] md:w-[600px] opacity-100 scale-100 z-20 shadow-2xl' 
                    : 'w-[60vw] md:w-[320px] opacity-40 scale-90 grayscale z-10 hover:opacity-60'
                }`}
                onClick={() => {
                  setActiveIndex(idx);
                  if (isActive && onPropertyClick) {
                    onPropertyClick(properties.find(p => p.id === prop.id)!);
                  }
                }}
              >
                <div className="aspect-3/4 relative overflow-hidden bg-stone-800">
                  <Image src={prop.image} alt={prop.name} fill className="object-cover" sizes="600px" />
                  
                  <HorizonOverlay isHovered={isActive} isFrance={prop.isFrance} />

                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 p-8 bg-linear-to-t from-stone-900 via-stone-900/60 to-transparent text-white z-40">
                      <h3 className="font-serif text-3xl">{prop.name}</h3>
                      <p className="text-xs uppercase tracking-widest mt-2">{prop.location}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Controls */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-4">
          <button 
            onClick={prev} 
            className="p-4 rounded-full bg-white/10 hover:bg-white text-white hover:text-stone-900 backdrop-blur-md transition-all border border-white/20"
          >
            <ChevronLeft size={24} />
          </button>
          <button 
            onClick={next} 
            className="p-4 rounded-full bg-white/10 hover:bg-white text-white hover:text-stone-900 backdrop-blur-md transition-all border border-white/20"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// 4. PROPERTY INDEX - Split Layout with Text List
// =============================================================================

export interface PropertyIndexProps {
  properties: PropertyNavigationItem[];
  locale?: string;
  title?: string;
  subtitle?: string;
  onPropertyClick?: (property: PropertyNavigationItem) => void;
  maxItems?: number;
}

/**
 * PropertyIndex - Split layout with text list + sticky image
 * 
 * Sample UI "The Index" (EXACT MATCH):
 * - Left side: property names as list
 * - Right side: sticky image that changes on hover
 * - Large typography with hover effects
 */
export function PropertyIndex({
  properties,
  locale = 'en',
  title,
  subtitle,
  onPropertyClick,
  maxItems = 10,
}: PropertyIndexProps) {
  const [hoveredIndex, setHoveredIndex] = useState(0);
  
  const formatted = useMemo(() => 
    properties.slice(0, maxItems).map(formatProperty), 
    [properties, maxItems]
  );

  if (formatted.length === 0) return null;

  return (
    <section className="w-full bg-stone-50">
      {(title || subtitle) && (
        <div className="container mx-auto px-6 md:px-12 py-16 text-center">
          {subtitle && <p className="text-stone-400 uppercase tracking-[0.3em] text-xs mb-4">{subtitle}</p>}
          {title && <h2 className="font-serif text-3xl md:text-4xl text-stone-900">{title}</h2>}
        </div>
      )}

      <div className="w-full flex flex-col md:flex-row min-h-[80vh]">
        {/* Left: List */}
        <div className="w-full md:w-1/2 p-6 md:p-12 flex flex-col justify-center">
          <div className="space-y-0">
            {formatted.map((prop, idx) => (
              <div 
                key={prop.id}
                className="group relative border-b border-stone-200 py-8 cursor-pointer transition-all duration-300 hover:pl-4"
                onMouseEnter={() => setHoveredIndex(idx)}
                onClick={() => onPropertyClick?.(properties.find(p => p.id === prop.id)!)}
              >
                <Link href={`/${locale}/properties/${prop.id}`} className="block">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs font-sans text-stone-400 group-hover:text-stone-900 transition-colors">
                      0{idx + 1}
                    </span>
                    <h3 className="font-serif text-3xl md:text-5xl text-stone-400 group-hover:text-stone-900 group-hover:italic transition-all duration-300">
                      {prop.name}
                    </h3>
                  </div>
                  <div className="flex justify-end mt-2 overflow-hidden h-0 group-hover:h-6 transition-all duration-300">
                    <p className="text-xs uppercase tracking-widest text-stone-500">
                      {prop.location}
                      {prop.price && (
                        <>
                          {' — '}
                          <FormatPrice amount={prop.price} currency={prop.currency} size="sm" className="text-stone-500! inline" />
                        </>
                      )}
                    </p>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Sticky Image */}
        <div className="hidden md:block w-1/2 h-[80vh] sticky top-24 p-6">
          <div className="w-full h-full relative overflow-hidden bg-stone-200">
            {formatted.map((prop, idx) => (
              <Image 
                key={prop.id}
                src={prop.image}
                alt={prop.name}
                fill
                className={`object-cover transition-all duration-700 ease-[0.22,1,0.36,1] ${
                  hoveredIndex === idx ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-110 z-0'
                }`}
                sizes="50vw"
              />
            ))}
            
            {/* Horizon Overlay */}
            <div className="absolute inset-0 z-30 pointer-events-none">
              <HorizonOverlay isHovered={true} isFrance={formatted[hoveredIndex]?.isFrance ?? true} />
            </div>

            {/* Overlay Info */}
            <div className="absolute bottom-8 left-8 text-white z-40">
              <p className="text-xs uppercase tracking-[0.3em] mb-2">
                {formatted[hoveredIndex]?.isFrance ? 'France' : 'Mauritius'}
              </p>
              <h2 className="font-serif text-4xl">{formatted[hoveredIndex]?.name}</h2>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// 5. PROPERTY PATHFINDER - Scrollytelling with Progress Line
// =============================================================================

export interface PropertyPathfinderProps {
  properties: PropertyNavigationItem[];
  locale?: string;
  title?: string;
  subtitle?: string;
  onPropertyClick?: (property: PropertyNavigationItem) => void;
  maxItems?: number;
}

/**
 * PropertyPathfinder - Scrollytelling with sticky image and progress line
 * 
 * Sample UI "The Pathfinder" (EXACT MATCH):
 * - Left side: sticky image with Ken Burns effect
 * - Right side: scrollable journey with progress line
 * - Active state with node highlights
 */
export function PropertyPathfinder({
  properties,
  locale = 'en',
  title,
  subtitle,
  onPropertyClick,
  maxItems = 10,
}: PropertyPathfinderProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  
  const formatted = useMemo(() => 
    properties.slice(0, maxItems).map(formatProperty), 
    [properties, maxItems]
  );

  // Intersection Observer for scroll-based activation
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = Number(entry.target.getAttribute('data-index'));
          setActiveIndex(idx);
        }
      });
    }, {
      threshold: 0.5,
      rootMargin: "0px 0px -20% 0px"
    });

    itemsRef.current.forEach(el => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [formatted]);

  if (formatted.length === 0) return null;

  return (
    <section className="w-full bg-stone-100">
      {(title || subtitle) && (
        <div className="container mx-auto px-6 md:px-12 py-16 text-center">
          {subtitle && <p className="text-stone-400 uppercase tracking-[0.3em] text-xs mb-4">{subtitle}</p>}
          {title && <h2 className="font-serif text-3xl md:text-4xl text-stone-900">{title}</h2>}
        </div>
      )}

      <div className="w-full flex flex-col md:flex-row min-h-screen">
        {/* Left: Sticky Image Area with Ken Burns */}
        <div className="hidden md:block w-1/2 h-screen sticky top-0 z-0">
          <div className="relative w-full h-full overflow-hidden bg-stone-900">
            {formatted.map((prop, idx) => {
              const isActive = activeIndex === idx;
              return (
                <div key={prop.id} className={`absolute inset-0 transition-opacity duration-1000 ease-[0.22,1,0.36,1] ${isActive ? 'opacity-100' : 'opacity-0'}`}>
                  <Image 
                    src={prop.image}
                    alt={prop.name}
                    fill
                    className={`object-cover transition-transform duration-4000 ease-out ${isActive ? 'scale-110 opacity-60' : 'scale-100 opacity-0'}`}
                    sizes="50vw"
                  />
                  
                  {/* Atmospheric Overlay */}
                  <div className="absolute inset-0 bg-stone-900/20" />

                  {/* Horizon Context */}
                  <HorizonOverlay isHovered={true} isFrance={prop.isFrance} />
                  
                  {/* Text Reveal */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
                    <div className="overflow-hidden">
                      <h2 className={`font-serif text-5xl md:text-7xl text-white text-center italic transition-transform duration-1000 ease-[0.22,1,0.36,1] ${isActive ? 'translate-y-0' : 'translate-y-full'}`}>
                        {prop.location}
                      </h2>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: The Journey Content */}
        <div className="w-full md:w-1/2 relative min-h-screen bg-stone-50 z-10">
          {/* The Line Base */}
          <div className="absolute left-8 md:left-12 top-0 bottom-0 w-px bg-stone-200">
            {/* The Active Line Progress */}
            <div 
              className="w-full bg-stone-900 transition-all duration-1000 ease-[0.22,1,0.36,1]"
              style={{ height: `${(activeIndex / Math.max(formatted.length - 1, 1)) * 100}%` }}
            />
          </div>

          <div className="py-24 md:py-48 px-8 md:px-24">
            {formatted.map((prop, idx) => {
              const isActive = activeIndex === idx;
              const originalProp = properties.find(p => p.id === prop.id);
              
              return (
                <div 
                  key={prop.id}
                  data-index={idx}
                  ref={(el) => { itemsRef.current[idx] = el; }}
                  className={`relative min-h-[60vh] md:min-h-[80vh] flex flex-col justify-center pl-12 group cursor-pointer transition-all duration-1000 ease-[0.22,1,0.36,1] ${isActive ? 'opacity-100 scale-100 blur-0' : 'opacity-30 scale-95 blur-[1px]'}`}
                  onClick={() => {
                    setActiveIndex(idx);
                    if (onPropertyClick && originalProp) onPropertyClick(originalProp);
                  }}
                >
                  {/* The Node on the Line */}
                  <div 
                    className={`absolute left-[-5px] md:left-[-21px] w-3 h-3 md:w-4 md:h-4 rounded-full border-2 transition-all duration-700 ease-[0.22,1,0.36,1] z-10 ${
                      isActive ? 'bg-stone-900 border-stone-900 scale-150 shadow-[0_0_0_8px_rgba(28,25,23,0.1)]' : 'bg-stone-50 border-stone-300'
                    }`}
                  />
                  
                  {/* Text Content with Stagger */}
                  <Link href={`/${locale}/properties/${prop.id}`} className={`transition-all duration-700 ease-[0.22,1,0.36,1] ${isActive ? 'translate-x-0' : 'translate-x-8'}`}>
                    <span className={`text-xs font-sans text-stone-400 uppercase tracking-widest mb-4 block transition-all duration-700 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                      Stop 0{idx + 1}
                    </span>
                    <h3 className={`font-serif text-4xl md:text-5xl text-stone-900 mb-6 leading-tight transition-all duration-700 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '100ms' }}>
                      {prop.name}
                    </h3>
                    <p className={`font-sans text-stone-500 leading-relaxed max-w-sm mb-8 transition-all duration-700 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '200ms' }}>
                      {prop.description || `Discover this exceptional property in ${prop.location}.`}
                    </p>
                    <div className={`flex items-center gap-4 text-xs font-medium uppercase tracking-widest transition-all duration-700 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '300ms' }}>
                      {prop.price && <FormatPrice amount={prop.price} currency={prop.currency} size="sm" className="text-stone-900!" />}
                      <span className="w-8 h-px bg-stone-300" />
                      <span className="group-hover:text-stone-900 transition-colors">View Details</span>
                    </div>
                  </Link>

                  {/* Scroll Hint (Only first item) */}
                  {idx === 0 && isActive && (
                    <div className="absolute bottom-12 left-12 animate-bounce opacity-40">
                      <ChevronDown />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// 6. PROPERTY DECK - 3D Card Stack
// =============================================================================

export interface PropertyDeckProps {
  properties: PropertyNavigationItem[];
  locale?: string;
  title?: string;
  subtitle?: string;
  onPropertyClick?: (property: PropertyNavigationItem) => void;
  maxItems?: number;
}

/**
 * PropertyDeck - 3D card stack with perspective transforms
 * 
 * Sample UI "The Deck" (EXACT MATCH):
 * - Stacked cards with 3D perspective
 * - Atmospheric blurred background
 * - Navigation controls with counter
 */
export function PropertyDeck({
  properties,
  locale = 'en',
  title,
  subtitle,
  onPropertyClick,
  maxItems = 10,
}: PropertyDeckProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  
  const formatted = useMemo(() => 
    properties.slice(0, maxItems).map(formatProperty), 
    [properties, maxItems]
  );

  const next = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % formatted.length);
  }, [formatted.length]);

  const prev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + formatted.length) % formatted.length);
  }, [formatted.length]);

  if (formatted.length === 0) return null;

  return (
    <section className="w-full bg-stone-950">
      {(title || subtitle) && (
        <div className="container mx-auto px-6 md:px-12 py-16 text-center">
          {subtitle && <p className="text-stone-500 uppercase tracking-[0.3em] text-xs mb-4">{subtitle}</p>}
          {title && <h2 className="font-serif text-3xl md:text-4xl text-white italic">{title}</h2>}
        </div>
      )}

      <div className="w-full h-screen flex flex-col items-center justify-center relative overflow-hidden">
        {/* Atmospheric Background Cross-fade */}
        <div className="absolute inset-0">
          {formatted.map((prop, idx) => (
            <div 
              key={prop.id}
              className={`absolute inset-0 transition-opacity duration-1500 ease-in-out ${activeIndex === idx ? 'opacity-40' : 'opacity-0'}`}
            >
              <Image src={prop.image} alt="" fill className="object-cover blur-3xl scale-125" />
            </div>
          ))}
          <div className="absolute inset-0 bg-stone-950/40 mix-blend-multiply" />
        </div>
        
        <div 
          className="relative z-10 w-full max-w-md aspect-3/4 flex items-center justify-center"
          style={{ perspective: '1000px' }}
        >
          {formatted.map((prop, idx) => {
            // Circular Deck Logic
            let offset = idx - activeIndex;
            if (offset < 0) offset += formatted.length;
            
            // Hide cards too far back
            if (offset > 4) return null;

            const isActive = offset === 0;

            // 3D Transforms
            const scale = 1 - (offset * 0.05);
            const translateY = offset * 40;
            const translateZ = -offset * 100;
            const rotateX = -offset * 2;
            const opacity = 1 - (offset * 0.15);
            const zIndex = 50 - offset;

            return (
              <div 
                key={prop.id}
                className="absolute inset-0 transition-all duration-800 ease-[0.22,1,0.36,1] rounded-sm shadow-2xl overflow-hidden cursor-pointer group origin-bottom"
                style={{ 
                  zIndex,
                  transform: `translate3d(0, ${translateY}px, ${translateZ}px) rotateX(${rotateX}deg) scale(${scale})`,
                  opacity,
                  transformStyle: 'preserve-3d'
                }}
                onClick={() => {
                  if (isActive && onPropertyClick) {
                    onPropertyClick(properties.find(p => p.id === prop.id)!);
                  } else {
                    setActiveIndex(idx);
                  }
                }}
              >
                <Link href={`/${locale}/properties/${prop.id}`} className="block w-full h-full">
                  <div className="relative w-full h-full bg-stone-100 flex flex-col">
                    {/* Image Area */}
                    <div className="relative h-3/4 overflow-hidden">
                      <Image src={prop.image} alt={prop.name} fill className="object-cover" sizes="400px" />
                      
                      {/* Dynamic Shine Effect */}
                      <div className="absolute inset-0 bg-linear-to-tr from-white/0 via-white/20 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ backgroundSize: '200% 200%' }} />
                      
                      {/* Shadow Gradient */}
                      <div className="absolute inset-0 bg-linear-to-t from-stone-900/80 to-transparent opacity-60" />

                      {/* Horizon Strip - Only on Active */}
                      {isActive && <HorizonOverlay isHovered={true} isFrance={prop.isFrance} />}
                    </div>
                    
                    {/* Card Body */}
                    <div className="h-1/4 p-8 flex flex-col justify-between bg-white relative">
                      <div>
                        <div className="flex justify-between items-start mb-1">
                          <h3 className="font-serif text-3xl text-stone-900 truncate pr-4">{prop.name}</h3>
                          <span className="text-xs font-sans text-stone-300">0{idx + 1}</span>
                        </div>
                        <p className="text-xs uppercase tracking-widest text-stone-500">{prop.location}</p>
                      </div>
                      <div className="flex justify-between items-center text-xs font-medium border-t border-stone-100 pt-4">
                        {prop.price && <FormatPrice amount={prop.price} currency={prop.currency} size="sm" className="text-stone-900!" />}
                        <div className="flex items-center gap-2 text-stone-400 group-hover:text-stone-900 transition-colors">
                          <span className="uppercase tracking-widest">Explore</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Glass Controls */}
        <div className="absolute bottom-12 flex gap-8 items-center z-50">
          <button 
            onClick={prev}
            className="w-14 h-14 rounded-full border border-white/20 bg-white/10 backdrop-blur-md flex items-center justify-center text-white hover:bg-white hover:text-stone-900 transition-all duration-300 group"
          >
            <RotateCcw size={20} className="group-hover:-rotate-180 transition-transform duration-500" />
          </button>
          
          <div className="flex flex-col items-center">
            <span className="font-serif italic text-2xl text-white">
              {activeIndex + 1} <span className="text-white/40 text-lg not-italic">/ {formatted.length}</span>
            </span>
          </div>

          <button 
            onClick={next}
            className="w-14 h-14 rounded-full border border-white/20 bg-white/10 backdrop-blur-md flex items-center justify-center text-white hover:bg-white hover:text-stone-900 transition-all duration-300 group"
          >
            <Layers size={20} className="group-hover:scale-110 transition-transform duration-300" />
          </button>
        </div>
      </div>
    </section>
  );
}
